/**
 * Background Sync Utility for Laprecio PWA
 * 
 * This utility provides functions to handle form submissions with background sync
 * for offline support. It uses IndexedDB to store form data when offline and
 * syncs it when the connection is restored.
 */

// Check if IndexedDB is supported
const isIndexedDBSupported = 'indexedDB' in window;

// Check if Background Sync API is supported
const isBackgroundSyncSupported = 'serviceWorker' in navigator && 'SyncManager' in window;

/**
 * Initialize IndexedDB for storing form data
 * @returns {Promise<IDBDatabase>} The IndexedDB database instance
 */
function initDB() {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBSupported) {
      reject(new Error('IndexedDB is not supported in this browser'));
      return;
    }

    const request = indexedDB.open('LaprecioFormsDB', 1);

    request.onerror = (event) => {
      reject(new Error('Error opening IndexedDB'));
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object stores for different form types
      if (!db.objectStoreNames.contains('contactForms')) {
        db.createObjectStore('contactForms', { keyPath: 'id', autoIncrement: true });
      }
      
      if (!db.objectStoreNames.contains('registrationForms')) {
        db.createObjectStore('registrationForms', { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

/**
 * Save form data to IndexedDB
 * @param {string} storeType - The type of form ('contactForms' or 'registrationForms')
 * @param {Object} formData - The form data to save
 * @returns {Promise<number>} The ID of the saved form data
 */
async function saveFormToIndexedDB(storeType, formData) {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeType], 'readwrite');
      const store = transaction.objectStore(storeType);
      
      // Add timestamp to the form data
      const formDataWithTimestamp = {
        ...formData,
        timestamp: new Date().toISOString()
      };
      
      const request = store.add(formDataWithTimestamp);
      
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      
      request.onerror = (event) => {
        reject(new Error('Error saving form data to IndexedDB'));
      };
    });
  } catch (error) {
    console.error('Error in saveFormToIndexedDB:', error);
    throw error;
  }
}

/**
 * Get all pending form submissions from IndexedDB
 * @param {string} storeType - The type of form ('contactForms' or 'registrationForms')
 * @returns {Promise<Array>} Array of pending form submissions
 */
async function getPendingForms(storeType) {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeType], 'readonly');
      const store = transaction.objectStore(storeType);
      const request = store.getAll();
      
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
      
      request.onerror = (event) => {
        reject(new Error('Error getting pending forms from IndexedDB'));
      };
    });
  } catch (error) {
    console.error('Error in getPendingForms:', error);
    throw error;
  }
}

/**
 * Delete a form submission from IndexedDB
 * @param {string} storeType - The type of form ('contactForms' or 'registrationForms')
 * @param {number} id - The ID of the form submission to delete
 * @returns {Promise<void>}
 */
async function deleteForm(storeType, id) {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeType], 'readwrite');
      const store = transaction.objectStore(storeType);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = (event) => {
        reject(new Error('Error deleting form from IndexedDB'));
      };
    });
  } catch (error) {
    console.error('Error in deleteForm:', error);
    throw error;
  }
}

/**
 * Submit a form with background sync support
 * @param {string} formType - The type of form ('contact' or 'registration')
 * @param {Object} formData - The form data to submit
 * @param {string} endpoint - The API endpoint to submit the form to
 * @returns {Promise<Object>} The response from the server or a status object
 */
export async function submitFormWithBackgroundSync(formType, formData, endpoint) {
  // Check if the browser is online
  if (navigator.onLine) {
    try {
      // If online, submit the form directly
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // If there's an error with the submission, save it for background sync
      if (isIndexedDBSupported) {
        const storeType = `${formType}Forms`;
        const id = await saveFormToIndexedDB(storeType, formData);
        
        // Register for background sync if supported
        if (isBackgroundSyncSupported) {
          const registration = await navigator.serviceWorker.ready;
          await registration.sync.register(`${formType}-form-sync`);
        }
        
        return {
          success: false,
          offline: true,
          message: 'You are offline. Your form has been saved and will be submitted when you are back online.'
        };
      }
      
      throw error;
    }
  } else {
    // If offline, save the form data for later submission
    if (isIndexedDBSupported) {
      const storeType = `${formType}Forms`;
      const id = await saveFormToIndexedDB(storeType, formData);
      
      // Register for background sync if supported
      if (isBackgroundSyncSupported) {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register(`${formType}-form-sync`);
      }
      
      return {
        success: false,
        offline: true,
        message: 'You are offline. Your form has been saved and will be submitted when you are back online.'
      };
    } else {
      throw new Error('You are offline and your browser does not support storing the form data for later submission.');
    }
  }
}

/**
 * Sync all pending forms of a specific type
 * @param {string} formType - The type of form ('contact' or 'registration')
 * @param {string} endpoint - The API endpoint to submit the forms to
 * @returns {Promise<Array>} Array of sync results
 */
export async function syncPendingForms(formType, endpoint) {
  try {
    const storeType = `${formType}Forms`;
    const pendingForms = await getPendingForms(storeType);
    
    if (pendingForms.length === 0) {
      return [];
    }
    
    const results = [];
    
    for (const form of pendingForms) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(form)
        });
        
        if (response.ok) {
          // If the submission was successful, delete the form from IndexedDB
          await deleteForm(storeType, form.id);
          results.push({
            id: form.id,
            success: true
          });
        } else {
          results.push({
            id: form.id,
            success: false,
            error: `Server responded with ${response.status}`
          });
        }
      } catch (error) {
        results.push({
          id: form.id,
          success: false,
          error: error.message
        });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error syncing pending forms:', error);
    throw error;
  }
}

/**
 * Get the count of pending forms
 * @param {string} formType - The type of form ('contact' or 'registration')
 * @returns {Promise<number>} The number of pending forms
 */
export async function getPendingFormsCount(formType) {
  try {
    const storeType = `${formType}Forms`;
    const pendingForms = await getPendingForms(storeType);
    return pendingForms.length;
  } catch (error) {
    console.error('Error getting pending forms count:', error);
    return 0;
  }
}

export default {
  submitFormWithBackgroundSync,
  syncPendingForms,
  getPendingFormsCount
};
